import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const PricingCards = () => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const headerRef = useScrollAnimation({ delay: 0 });
  const card1Ref = useScrollAnimation({ delay: 0 });
  const card2Ref = useScrollAnimation({ delay: 150 });
  const card3Ref = useScrollAnimation({ delay: 300 });
  const noteRef = useScrollAnimation({ delay: 400 });

  const packages = [
    {
      icon: 'ri-building-line',
      tier: 'Foundation',
      title: 'Lift Your Legacy Foundation',
      subtitle: 'Build the structure. Establish the standard.',
      bestFor: 'Beginners to intermediate who need accountability and structure.',
      price: '$297',
      priceSuffix: '/ month',
      originalValue: '$1,200',
      accentColor: '#B8860B',
      image: 'https://public.readdy.ai/ai/img_res/d5b8701ee71f62c7cae3a72cde3b2c3a.jpg',
      features: [
        'Personalized training program',
        'Nutrition blueprint',
        'Monthly program adjustments',
        'Habit and discipline tracker',
        'Bi weekly check ins',
        'Access to private community',
        'Lift Your Legacy Starter Guide',
      ],
      bonuses: [
        { item: 'Meal Plan Template', value: 49 },
        { item: 'Workout Guide PDF', value: 29 },
        { item: 'Habit Tracker Spreadsheet', value: 39 },
      ],
      badge: null,
      urgency: null,
      cta: 'Start Your Foundation',
      highlight: false,
      ref: card1Ref,
    },
    {
      icon: 'ri-arrow-up-circle-line',
      tier: 'Ascend',
      title: 'Lift Your Legacy Ascend',
      subtitle: 'Elevate your body. Elevate your discipline.',
      bestFor: 'Professionals who want faster results and direct coaching.',
      price: '$597',
      priceSuffix: '/ month',
      originalValue: '$2,400',
      accentColor: '#8B0000',
      image: 'https://readdy.ai/api/search-image?query=confident%20professional%20man%20in%20premium%20gym%20setting%20performance%20training%20executive%20lifestyle%20clean%20modern%20background%20sharp%20focused%20athletic%20physique%20high%20contrast%20lighting&width=600&height=500&seq=pkg2&orientation=landscape',
      features: [
        'Everything in Foundation',
        'Custom macro planning',
        'Weekly check ins',
        'Form review and video feedback',
        'Performance tracking dashboard',
        'Lifestyle optimization plan',
        'Direct message support',
        'Mindset training modules',
      ],
      bonuses: [
        { item: 'Recipe Book (50+ Recipes)', value: 79 },
        { item: 'Supplement Guide', value: 39 },
        { item: 'Mindset Mastery Course', value: 97 },
        { item: 'Meal Prep Masterclass', value: 67 },
      ],
      badge: 'Most Popular',
      urgency: null,
      cta: 'Begin Your Ascent',
      highlight: true,
      ref: card2Ref,
    },
    {
      icon: 'ri-vip-crown-line',
      tier: 'Legacy Elite',
      title: 'Lift Your Legacy Elite',
      subtitle: 'Train like the person you are becoming.',
      bestFor: 'Executives, entrepreneurs, and high performers who demand the best.',
      price: '$1,500',
      priceSuffix: '/ month',
      originalValue: '$5,000',
      accentColor: '#1a1a1a',
      image: 'https://readdy.ai/api/search-image?query=diverse%20group%20of%20elite%20athletes%20training%20together%20in%20premium%20private%20gym%20luxury%20fitness%20coaching%20high%20performance%20transformation%20men%20and%20women%20powerful%20physiques%20sophisticated%20atmosphere%20teamwork&width=600&height=500&seq=pkg3div&orientation=landscape',
      features: [
        'Everything in Ascend',
        '1 on 1 weekly video calls',
        'Advanced physique strategy',
        'Executive performance coaching',
        'Stress and recovery optimization',
        'Bloodwork and supplement guidance',
        'Priority access and scheduling',
        'VIP private access channel',
        'Quarterly performance reset plan',
      ],
      bonuses: [
        { item: 'Advanced Training Manual', value: 147 },
        { item: 'Premium Supplement Stack Guide', value: 200 },
        { item: 'Executive Wellness Blueprint', value: 297 },
        { item: 'Lifetime Recipe and Program Updates', value: 497 },
        { item: 'VIP Retreat Access', value: 497 },
      ],
      badge: 'VIP Elite',
      urgency: 'Only 3 spots available this month',
      cta: 'Apply for Legacy Elite',
      highlight: false,
      ref: card3Ref,
    },
  ];

  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-16">
          <p className="text-xs font-bold tracking-[0.35em] uppercase mb-3" style={{ color: '#8B0000' }}>
            Coaching Packages
          </p>
          <h2 className="text-4xl md:text-5xl font-extrabold text-black mb-5 leading-tight">
            Choose Your Level of{' '}
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: 'linear-gradient(90deg, #B8860B, #F5D76E)' }}
            >
              Commitment
            </span>
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Every package is built for people who are serious. Pick the tier that matches where you are and where you are going.
          </p>
          <div className="w-16 h-1 mx-auto rounded-full mt-6" style={{ background: 'linear-gradient(90deg, #B8860B, #F5D76E)' }} />
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {packages.map((pkg, index) => (
            <div
              key={index}
              ref={pkg.ref}
              className="relative rounded-2xl overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl cursor-pointer"
              style={{
                background: '#ffffff',
                border: pkg.highlight ? `2px solid ${pkg.accentColor}` : '1px solid #ebebeb',
                boxShadow: pkg.highlight
                  ? '0 8px 40px rgba(139,0,0,0.15)'
                  : hoveredIdx === index
                  ? '0 12px 40px rgba(0,0,0,0.12)'
                  : '0 2px 16px rgba(0,0,0,0.06)',
              }}
              onMouseEnter={() => setHoveredIdx(index)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              {/* Badge */}
              {pkg.badge && (
                <div
                  className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase whitespace-nowrap"
                  style={{
                    background: pkg.badge === 'Most Popular' ? '#8B0000' : '#B8860B',
                    color: '#ffffff',
                  }}
                >
                  {pkg.badge}
                </div>
              )}

              {/* Image */}
              <div className="w-full h-52 overflow-hidden">
                <img
                  src={pkg.image}
                  alt={pkg.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                />
              </div>

              <div className="flex flex-col flex-1 p-7">
                {/* Tier Label */}
                <div className="flex items-center gap-2 mb-4">
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-full"
                    style={{ background: `${pkg.accentColor}18` }}
                  >
                    <i className={`${pkg.icon} text-base`} style={{ color: pkg.accentColor }}></i>
                  </div>
                  <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: pkg.accentColor }}>
                    {pkg.tier}
                  </span>
                </div>

                {/* Title & Subtitle */}
                <h3 className="text-xl font-extrabold mb-1 leading-snug text-black">{pkg.title}</h3>
                <p className="text-xs italic mb-5" style={{ color: '#999999' }}>{pkg.subtitle}</p>

                {/* Price */}
                <div className="mb-1">
                  <span className="text-xs line-through" style={{ color: '#aaaaaa' }}>
                    Worth {pkg.originalValue}
                  </span>
                </div>
                <div className="flex items-end gap-1 mb-2">
                  <span className="text-4xl font-black" style={{ color: pkg.accentColor }}>
                    {pkg.price}
                  </span>
                  <span className="text-sm font-medium mb-1" style={{ color: '#888888' }}>
                    {pkg.priceSuffix}
                  </span>
                </div>

                <p className="text-xs mb-5 leading-relaxed" style={{ color: '#777777' }}>
                  <span className="font-bold" style={{ color: '#333333' }}>Best for: </span>
                  {pkg.bestFor}
                </p>

                {/* Urgency */}
                {pkg.urgency && (
                  <div className="mb-5 p-3 rounded-lg" style={{ background: '#fff0f0', border: '1px solid #f5c0c0' }}>
                    <p className="text-xs font-semibold text-center flex items-center justify-center gap-2" style={{ color: '#8B0000' }}>
                      <span className="w-2 h-2 rounded-full animate-pulse inline-block" style={{ background: '#8B0000' }}></span>
                      {pkg.urgency}
                    </p>
                  </div>
                )}

                {/* CTA Button */}
                <button
                  onClick={() => {
                    const el = document.getElementById('booking-form');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full py-3.5 rounded-lg font-bold text-sm transition-all duration-300 whitespace-nowrap mb-6 cursor-pointer"
                  style={{
                    background: pkg.highlight ? pkg.accentColor : 'transparent',
                    color: pkg.highlight ? '#ffffff' : pkg.accentColor,
                    border: `2px solid ${pkg.accentColor}`,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = pkg.accentColor;
                    e.currentTarget.style.color = '#ffffff';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = pkg.highlight ? pkg.accentColor : 'transparent';
                    e.currentTarget.style.color = pkg.highlight ? '#ffffff' : pkg.accentColor;
                  }}
                >
                  {pkg.cta}
                </button>

                <div className="w-full h-px mb-5" style={{ background: '#f0f0f0' }} />

                {/* Features */}
                <div className="mb-6 flex-grow">
                  <h4 className="font-bold text-black mb-3 text-xs uppercase tracking-wide">What's Included:</h4>
                  <ul className="space-y-2">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: '#444444' }}>
                        <span className="mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-sm font-bold" style={{ color: pkg.accentColor }}></i>
                        </span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Bonuses */}
                <div className="border-t pt-5" style={{ borderColor: '#f0f0f0' }}>
                  <h4 className="font-bold text-black mb-3 text-xs uppercase tracking-wide">Bonus Value:</h4>
                  <ul className="space-y-2">
                    {pkg.bonuses.map((bonus, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <i className="ri-gift-line text-lg flex-shrink-0 w-4 h-4 flex items-center justify-center" style={{ color: '#8B0000' }}></i>
                        <div>
                          <span style={{ color: '#555555' }}>{bonus.item}</span>
                          <span className="font-semibold ml-1" style={{ color: '#B8860B' }}>(${bonus.value} value)</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: '#f0f0f0' }}>
                    <p className="text-sm font-bold text-black">
                      Total Bonus Value:{' '}
                      <span style={{ color: '#8B0000' }}>
                        ${pkg.bonuses.reduce((sum, b) => sum + b.value, 0).toLocaleString()}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div ref={noteRef} className="mt-12 text-center">
          <p className="text-sm text-gray-500 max-w-3xl mx-auto">
            All packages include our{' '}
            <span className="font-bold text-black">90 Day Results Guarantee</span>.
            Not happy? Get a full refund — no questions asked.
          </p>
          <p className="text-xs mt-3" style={{ color: '#aaaaaa' }}>
            Not sure which package is right for you?{' '}
            <button
              onClick={() => {
                const el = document.getElementById('booking-form');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="font-bold underline cursor-pointer"
              style={{ color: '#B8860B' }}
            >
              Book a free discovery call
            </button>{' '}
            and we will find the right fit together.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingCards;